import {usePortal} from 'components/portal/portal'
import {useQuerySb} from 'utils/db/useQuerySb'
import {useMemo} from 'react'
import {
  ReactionCfg,
  ReactionRow,
  ReactionRowForSave,
  reactionSummarySb,
  saveReactionAndUpdateSummary,
  zReactionSummary
} from './reactions'
import {QuestionCfg} from 'shared/forecast'
import {ProfileUI} from 'components/portal/profileTypes'

export const useParticipation = ({
  question_id,
  cfg
}: {
  question_id: string
  cfg: Pick<QuestionCfg, 'tasked' | 'taskedSlots'> | undefined
}) => {
  const {profile, workspace_id} = usePortal()
  const {data, setQueryData} = useQuerySb(
    'reaction',
    ['useParticipation', {workspace_id}],
    async () => await reactionSummarySb({workspace_id, types: ['participation']}),
    {
      enabled: profile.workspace.advancedUI,
      disableRealtime: true,
      select: rs => rs.filter(r => r.question_id == question_id)
    }
  )

  const participation = useMemo(() => {
    if (data == null) return undefined
    return data[0] ?? zReactionSummary.parse({type: 'participation', question_id})
  }, [data, question_id])

  const save = async (cfg: Omit<ReactionCfg<'participation'>, 'type'>) =>
    await saveReactionAndUpdateSummary({
      row: {question_id, created_by: profile.id, cfg: {type: 'participation', ...cfg}},
      setQueryData: setQueryData as any
    })

  const isParticipant = useMemo(() => {
    if (participation == null) return undefined
    return participation.participants.includes(profile.id)
  }, [participation, profile.id])

  return {participation, save, isTasked: cfg ? isQuestionTasked(profile, cfg) : undefined, isParticipant, cfg}
}
export type UseParticipation = ReturnType<typeof useParticipation>
export type Participation = UseParticipation['participation']

export const isQuestionTasked = (profile: Pick<ProfileUI, 'id'>, cfg: Pick<QuestionCfg, 'tasked'>) =>
  cfg.tasked.includes(profile.id)
